import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import PhotoIcon from '@mui/icons-material/Photo';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import type { DailyLog, DailyLogPhoto } from '../api/types';
import { formatDate } from '../lib/format';
import {
  useAuthedPhotoSrc,
  useDeleteLogPhoto,
  useUpdateDailyLog,
  useUpdatePhotoCaption,
  useUploadLogPhoto,
} from '../hooks/useOps';

interface Props {
  logs: DailyLog[];
  isLoading?: boolean;
  unitLabel?: string;
  /** Enables add/delete/caption-edit on photos (W1). Off by default. */
  canEdit?: boolean;
}

interface LightboxState {
  photo: DailyLogPhoto;
  trackId: string;
  /** Resolved blob object-URL, handed over by the thumbnail that opened it. */
  src: string;
}

interface EditDraft {
  log: DailyLog;
  reportedDate: string;
  amount: string;
  note: string;
}

export function DailyLogTimeline({ logs, isLoading, unitLabel = 'units', canEdit = false }: Props) {
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [captionDraft, setCaptionDraft] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const addInputRef = useRef<HTMLInputElement>(null);
  const [addTarget, setAddTarget] = useState<DailyLog | null>(null);
  const [edit, setEdit] = useState<EditDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const uploadPhoto = useUploadLogPhoto();
  const updateCaption = useUpdatePhotoCaption();
  const deletePhoto = useDeleteLogPhoto();
  const updateLog = useUpdateDailyLog();

  if (isLoading) {
    return (
      <Stack gap={1}>
        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: 1 }} />)}
      </Stack>
    );
  }

  if (!logs.length) {
    return (
      <Typography color="text.secondary" variant="body2">
        No logs yet.
      </Typography>
    );
  }

  const openLightbox = (photo: DailyLogPhoto, trackId: string, src: string) => {
    setLightbox({ photo, trackId, src });
    setCaptionDraft(photo.caption ?? '');
    setConfirmDelete(false);
  };

  const onAddFiles = async (files: FileList | null) => {
    if (!files || !addTarget) return;
    for (const file of Array.from(files)) {
      await uploadPhoto
        .mutateAsync({ logId: addTarget.id, trackId: addTarget.trackId, file })
        .catch(() => undefined); // per-file failure must not abort the rest
    }
    setAddTarget(null);
    if (addInputRef.current) addInputRef.current.value = '';
  };

  return (
    <>
      <Stack divider={<Divider />} gap={0}>
        {logs.map((log) => (
          <Box key={log.id} sx={{ py: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Stack direction="row" gap={1} alignItems="center">
                {log.isBlocked && (
                  <Chip
                    icon={<BlockIcon />}
                    label="Blocked"
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                )}
                {!log.isBlocked && (
                  <Chip
                    label={`+${log.progressIncrement} ${unitLabel}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {log.hasProblem && (
                  <Chip label="Problem" size="small" color="warning" variant="outlined" />
                )}
              </Stack>
              <Stack direction="row" gap={0.5} alignItems="center">
                {canEdit && (
                  <Tooltip title="Edit this entry">
                    <IconButton
                      size="small"
                      data-testid={`edit-log-${log.id}`}
                      onClick={() => {
                        setEditError(null);
                        setEdit({
                          log,
                          reportedDate: log.reportedDate,
                          amount: String(log.progressIncrement),
                          note: log.note ?? '',
                        });
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {canEdit && (
                  <Tooltip title="Add photos to this entry">
                    <IconButton
                      size="small"
                      data-testid={`add-photo-${log.id}`}
                      onClick={() => {
                        setAddTarget(log);
                        addInputRef.current?.click();
                      }}
                    >
                      <AddPhotoAlternateIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatDate(log.reportedDate)}
                </Typography>
              </Stack>
            </Stack>
            {log.note && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 0.5 }}>
                {log.note}
              </Typography>
            )}
            {(log.photos ?? []).length > 0 && (
              <Stack direction="row" gap={0.5} mt={0.5} flexWrap="wrap">
                {(log.photos ?? []).map((p) => (
                  <LogPhotoThumb
                    key={p.id}
                    photo={p}
                    onOpen={(src) => openLightbox(p, log.trackId, src)}
                  />
                ))}
                <Chip
                  icon={<PhotoIcon />}
                  label={`${(log.photos ?? []).length}`}
                  size="small"
                  variant="outlined"
                  sx={{ alignSelf: 'center' }}
                />
              </Stack>
            )}
          </Box>
        ))}
      </Stack>

      {/* hidden picker for "add photos to existing entry" */}
      <input
        ref={addInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        hidden
        onChange={(e) => void onAddFiles(e.target.files)}
      />

      {/* edit an existing entry: date, amount, note (blocked entries keep amount 0) */}
      <Dialog open={Boolean(edit)} onClose={() => setEdit(null)} maxWidth="xs" fullWidth>
        {edit && (
          <>
            <DialogContent>
              <Stack gap={2} mt={0.5}>
                <TextField
                  size="small"
                  type="date"
                  label="Date"
                  value={edit.reportedDate}
                  onChange={(e) => setEdit((d) => d && { ...d, reportedDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
                {!edit.log.isBlocked && (
                  <TextField
                    size="small"
                    type="number"
                    label={`Amount (${unitLabel})`}
                    value={edit.amount}
                    onChange={(e) => setEdit((d) => d && { ...d, amount: e.target.value })}
                    error={!(parseInt(edit.amount, 10) > 0)}
                    helperText={!(parseInt(edit.amount, 10) > 0) ? 'must be a whole number > 0' : undefined}
                    inputProps={{ min: 1 }}
                  />
                )}
                <TextField
                  size="small"
                  label="Note"
                  value={edit.note}
                  onChange={(e) => setEdit((d) => d && { ...d, note: e.target.value })}
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 2000 }}
                />
                {editError && (
                  <Typography variant="caption" color="error">
                    {editError}
                  </Typography>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button size="small" onClick={() => setEdit(null)}>
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={
                  updateLog.isPending ||
                  !edit.reportedDate ||
                  (!edit.log.isBlocked && !(parseInt(edit.amount, 10) > 0))
                }
                onClick={async () => {
                  const body: Partial<{
                    reportedDate: string;
                    progressIncrement: number;
                    note: string;
                  }> = {};
                  if (edit.reportedDate !== edit.log.reportedDate) body.reportedDate = edit.reportedDate;
                  const amount = parseInt(edit.amount, 10);
                  if (!edit.log.isBlocked && amount !== edit.log.progressIncrement) {
                    body.progressIncrement = amount;
                  }
                  if (edit.note !== (edit.log.note ?? '')) body.note = edit.note;
                  if (Object.keys(body).length === 0) {
                    setEdit(null);
                    return;
                  }
                  try {
                    await updateLog.mutateAsync({ logId: edit.log.id, trackId: edit.log.trackId, body });
                    setEdit(null);
                  } catch (e) {
                    setEditError((e as Error).message ?? 'Failed to save the entry');
                  }
                }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="md">
        <DialogContent sx={{ p: 0 }}>
          {lightbox && (
            <Box
              component="img"
              src={lightbox.src}
              alt={lightbox.photo.caption ?? 'full size'}
              sx={{ maxWidth: '90vw', maxHeight: '70vh', display: 'block' }}
            />
          )}
        </DialogContent>
        {lightbox && (canEdit ? (
          <DialogActions sx={{ px: 2, pb: 1.5, gap: 1, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Caption"
              value={captionDraft}
              onChange={(e) => setCaptionDraft(e.target.value)}
              inputProps={{ maxLength: 500 }}
              sx={{ flex: 1, minWidth: 220 }}
            />
            <Button
              size="small"
              variant="contained"
              disabled={updateCaption.isPending || captionDraft === (lightbox.photo.caption ?? '')}
              onClick={async () => {
                await updateCaption.mutateAsync({
                  photoId: lightbox.photo.id,
                  trackId: lightbox.trackId,
                  caption: captionDraft.trim() || null,
                });
                setLightbox((l) => l && { ...l, photo: { ...l.photo, caption: captionDraft.trim() || null } });
              }}
            >
              Save caption
            </Button>
            {confirmDelete ? (
              <Button
                size="small"
                color="error"
                variant="contained"
                disabled={deletePhoto.isPending}
                onClick={async () => {
                  await deletePhoto.mutateAsync({ photoId: lightbox.photo.id, trackId: lightbox.trackId });
                  setLightbox(null);
                }}
              >
                Confirm delete
              </Button>
            ) : (
              <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </DialogActions>
        ) : (
          lightbox.photo.caption && (
            <DialogActions sx={{ px: 2, pb: 1.5, justifyContent: 'flex-start' }}>
              <Typography variant="body2" color="text.secondary">{lightbox.photo.caption}</Typography>
            </DialogActions>
          )
        ))}
      </Dialog>
    </>
  );
}

/**
 * One timeline thumbnail. Fetches the photo with the bearer token and renders
 * a blob object-URL (a raw /api/photos URL in an <img src> 401s — the route is
 * auth-guarded and images can't carry the Authorization header). Own component
 * so the hook runs once per photo (rules of hooks).
 */
function LogPhotoThumb({ photo, onOpen }: { photo: DailyLogPhoto; onOpen: (src: string) => void }) {
  const src = useAuthedPhotoSrc(photo.filePath);
  if (!src) {
    return <Skeleton variant="rounded" width={56} height={56} sx={{ borderRadius: 1 }} />;
  }
  return (
    <Tooltip title={photo.caption ?? ''}>
      <Box
        component="img"
        src={src}
        alt={photo.caption ?? 'progress'}
        onClick={() => onOpen(src)}
        sx={{
          width: 56,
          height: 56,
          objectFit: 'cover',
          borderRadius: 1,
          cursor: 'pointer',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />
    </Tooltip>
  );
}
